import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, startWith } from 'rxjs';

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'ts-chip-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatChipsModule, MatAutocompleteModule, MatIconModule],
  templateUrl: './ts-chip-autocomplete.component.html',
  styleUrls: ['./ts-chip-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TsChipAutocompleteComponent),
      multi: true,
    },
  ],
})
export class TsChipAutocompleteComponent<T = any> implements ControlValueAccessor, OnInit {
  // -- Appearance --
  label = input<string>();
  placeholder = input<string>();
  hint = input<string>();
  testId = input<string>();

  // -- Mode --
  // 'combined' adds typed text on a separator key while still offering suggestions.
  mode = input<'autocomplete' | 'manual' | 'combined'>('autocomplete');

  // -- Autocomplete mode inputs --
  items = input<T[]>([]);
  searchProperties = input<string[]>([]);
  // The parent filters: listen to (search) and pass the results back as items, shown as given.
  remoteSearch = input(false);

  // -- Display --
  displayWith = input<(item: T) => string>();
  displayProperty = input<string>();
  chipDisplayProperty = input<string>();
  uniqueProperty = input<string>();
  invalidItemMessage = input<string>();

  // -- Manual mode --
  separatorKeys = input<number[]>([ENTER, COMMA]);
  validateItem = input<(item: T) => boolean>(() => true);
  transformItem = input<(item: T) => T>((item) => item);

  // -- Events --
  itemAdded = output<T>();
  itemRemoved = output<T>();
  search = output<string>();

  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger?: MatAutocompleteTrigger;

  // Internal state
  selectedItems = signal<T[]>([]);
  filteredItems$: Observable<T[]>;
  searchControl = new FormControl('');
  isFocused = false;
  disabled = signal(false);

  private onChange: (value: T[]) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  private readonly destroyRef = inject(DestroyRef);
  private readonly items$ = toObservable(this.items);

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const searchTerm$ = this.searchControl.valueChanges.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged());

    // Items are combined in so results that arrive after the term (a remote search) still reach the panel.
    this.filteredItems$ = combineLatest([searchTerm$.pipe(startWith('')), this.items$]).pipe(
      map(([term]) => this.filterItems(term)),
    );

    searchTerm$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.search.emit(typeof term === 'string' ? term : ''));
  }

  // ControlValueAccessor implementation
  writeValue(value: T[]): void {
    this.selectedItems.set(value ?? []);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  hasInvalidItems = computed(() => this.selectedItems().some((item) => !this.validateItem()(item)));

  onBlur(): void {
    this.isFocused = false;
    this.onTouchedCallback();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);

    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  private filterItems(term: string | any): T[] {
    const items = this.items() ?? [];
    if (this.remoteSearch() || !term || typeof term !== 'string') {
      return items.filter((i) => !this.isSelected(i));
    }
    const lower = term.toLowerCase();
    return items.filter(
      (i) =>
        !this.isSelected(i) &&
        this.searchProperties().some((p) => {
          const value = (i as any)[p];
          return value?.toString().toLowerCase().includes(lower);
        }),
    );
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value;

    if (!this.isSelected(item)) {
      const transform = this.transformItem();
      const transformedItem = transform ? transform(item) : item;
      this.selectedItems.update((items) => [...items, transformedItem]);
      this.emitChange();
      this.itemAdded.emit(transformedItem);
    }

    this.searchControl.setValue('');
  }

  onTokenEnd(event: MatChipInputEvent): void {
    if (this.mode() === 'autocomplete') return;

    // Enter on a highlighted suggestion also reaches the chip input; the option selection adds that chip.
    if (this.autocompleteTrigger?.activeOption) return;

    const value = event.value?.trim();
    if (!value) return;

    const newItem = this.toItem(value);

    if (!this.isSelected(newItem)) {
      this.selectedItems.update((items) => [...items, newItem]);
      this.emitChange();
      this.itemAdded.emit(newItem);
    }

    event.chipInput.clear();
    this.searchControl.setValue('');
  }

  removeItem(item: T): void {
    this.selectedItems.update((items) => items.filter((i) => this.getUniqueValue(i) !== this.getUniqueValue(item)));
    this.emitChange();
    this.itemRemoved.emit(item);
  }

  // Without a displayProperty the items are plain strings.
  private toItem(value: string): T {
    const prop = this.displayProperty();

    return (prop ? { [prop]: value } : value) as T;
  }

  private isSelected(item: T): boolean {
    const uniqueValue = this.getUniqueValue(item);
    return this.selectedItems().some((i) => this.getUniqueValue(i) === uniqueValue);
  }

  getUniqueValue(item: T): any {
    const prop = this.uniqueProperty();
    return prop ? (item as any)[prop] : item;
  }

  getDisplayValue(item: T): string {
    const displayWith = this.displayWith();
    if (displayWith) return displayWith(item);

    const prop = this.displayProperty();
    return prop ? (item as any)[prop] : String(item);
  }

  getChipDisplayValue(item: T): string {
    const prop = this.chipDisplayProperty() ?? this.displayProperty();
    return prop ? (item as any)[prop] : String(item);
  }

  private emitChange(): void {
    this.onChange(this.selectedItems());
    this.onTouchedCallback();
    this.cdr.markForCheck();
  }
}
